// ABOUTME: React Query hook for fetching pump settings configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/pump-configurations, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {PumpSettingsApiResponse, PumpSettingsConfig, PumpSettingsFormValues} from '../types/pumpSettings'
import {asNotInherited, emptyApiRow} from '../utils/emptyConfig'

const transformResponse = (response: PumpSettingsApiResponse): PumpSettingsConfig => {
  const availablePumps = response.availablePumps?.value
    ? {
        pumpSizeUomId: response.availablePumps.value.pumpSizeUom.id,
        defaultPumpCode: response.availablePumps.value.defaultPumpCode,
        pumps: response.availablePumps.value.pumps.map((p) => ({
          code: p.code,
          size: p.size,
          minLeadTime: p.minLeadTime,
          leadTimeUomId: p.leadTimeUom.id,
          referenceEvent: p.referenceEvent,
        })),
      }
    : null
  const hoseLength = response.hoseLength
    ? {
        uomId: response.hoseLength.uomId,
        default: response.hoseLength.default,
        min: response.hoseLength.min,
        max: response.hoseLength.max,
        step: response.hoseLength.step,
      }
    : null
  const defaultAnswers = response.defaultAnswers
    ? {
        canLargerPumpFit: response.defaultAnswers.canLargerPumpFit ?? {isOverridden: false, value: null},
        isPossibleToCleanPumpOnSite: response.defaultAnswers.isPossibleToCleanPumpOnSite ?? {isOverridden: false, value: null},
        isExtraHoseNeeded: response.defaultAnswers.isExtraHoseNeeded ?? {isOverridden: false, value: null},
        defaultDischarge: response.defaultAnswers.defaultDischarge ?? {isOverridden: false, value: null},
      }
    : null
  const ownFormValues: PumpSettingsFormValues = {availablePumps, hoseLength, defaultAnswers}
  return {
    id: response.id,
    availablePumps,
    hoseLength,
    defaultAnswers,
    overrides: {
      availablePumps: response.availablePumps?.isOverridden ?? false,
      hoseLength: response.hoseLength !== null && (
        response.hoseLength.default?.isOverridden === true ||
        response.hoseLength.min?.isOverridden === true ||
        response.hoseLength.max?.isOverridden === true ||
        response.hoseLength.step?.isOverridden === true
      ),
      defaultAnswers: response.defaultAnswers !== null && (
        response.defaultAnswers.canLargerPumpFit?.isOverridden === true ||
        response.defaultAnswers.isPossibleToCleanPumpOnSite?.isOverridden === true ||
        response.defaultAnswers.isExtraHoseNeeded?.isOverridden === true ||
        response.defaultAnswers.defaultDischarge?.isOverridden === true
      ),
    },
    parentValues: ownFormValues,
    _meta: {
      scopeId: response.scopeId,
      isInherited: !(
        (response.availablePumps?.isOverridden ?? false) ||
        (response.hoseLength !== null && (
          response.hoseLength.default?.isOverridden === true ||
          response.hoseLength.min?.isOverridden === true ||
          response.hoseLength.max?.isOverridden === true ||
          response.hoseLength.step?.isOverridden === true
        )) ||
        (response.defaultAnswers !== null && (
          response.defaultAnswers.canLargerPumpFit?.isOverridden === true ||
          response.defaultAnswers.isPossibleToCleanPumpOnSite?.isOverridden === true ||
          response.defaultAnswers.isExtraHoseNeeded?.isOverridden === true ||
          response.defaultAnswers.defaultDischarge?.isOverridden === true
        ))
      ),
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}

export const usePumpSettingsConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: PumpSettingsConfig | undefined; isLoading: boolean; error: Error | null} => {
  const normalizedBusinessLine = businessLine?.toUpperCase()
  const {data: rawData, isLoading, error} = useQuery<PumpSettingsApiResponse[], Error>({
    queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine, scopeId, rootScopeId],
    queryFn: async () => {
      const res = await api.get<PumpSettingsApiResponse[]>('/tuc-configs/pump-configurations', {
        params: {
          countryId,
          businessLine: normalizedBusinessLine,
          scopeId,
          rootScopeId,
          resolve: true,
        },
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootRawConfig = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const rootConfig = rootRawConfig ? transformResponse(rootRawConfig) : undefined
    const parentValues = rootConfig
      ? {availablePumps: rootConfig.availablePumps, hoseLength: rootConfig.hoseLength, defaultAnswers: rootConfig.defaultAnswers}
      : undefined

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) {
      const config = transformResponse(match)
      return parentValues ? {...config, parentValues} : config
    }

    if (!rootRawConfig || !rootConfig) {
      const empty = emptyApiRow<PumpSettingsApiResponse>(scopeId, {hoseLength: null, defaultAnswers: null})
      return asNotInherited(transformResponse(empty))
    }

    return {
      ...rootConfig,
      id: undefined,
      parentValues: parentValues ?? {availablePumps: rootConfig.availablePumps, hoseLength: rootConfig.hoseLength, defaultAnswers: rootConfig.defaultAnswers},
      _meta: {...rootConfig._meta, isInherited: true},
      overrides: {availablePumps: false, hoseLength: false, defaultAnswers: false},
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
